import React, { useState, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { 
  addReview, 
  updateReview, 
  deleteReview,
  reviewsSelectors,
  ReduxReview
} from '@/store/slices/userSpecificSlice';
import { selectUser } from '@/store/slices/authSlice';
import { 
  selectAllBrands, 
  selectAllDrinks, 
} from '@/store/slices/globalDataSlice';
import { selectAllLocations } from '@/store/slices/locationsSlice';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { CalendarIcon, MapPin, Trash2 } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import StarRating from '@/components/ui/star-rating';

// Import regular components (still used outside of popovers)
import { Calendar } from '@/components/ui/calendar';

// Import dialog-friendly components for nested UI
import { PopoverDialog, PopoverDialogContent, PopoverDialogTrigger } from '@/components/ui/popover-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import { format } from 'date-fns';
import { toast } from 'sonner';
import type { Drink, FirestoreLocation } from '@/types/firestore';

// Import the modals
import { AddBrandModal } from '@/components/sightings/AddBrandModal';
import { AddDrinkModal } from '@/components/sightings/AddDrinkModal';
import { SingleSelect } from '@/components/ui/single-select';
import LocationSelect from '@/components/locations/LocationSelect';

interface DiaryEntryPageProps {
  entryId?: string;
  onClose?: () => void;
}

// Form interface
interface ReviewFormData {
  brandId: string;
  brandName: string;
  drinkId: string;
  drinkName: string;
  rating: number;
  reviewText: string;
  isPublic: boolean;
  locationId?: string;
  locationName?: string;
  userId?: string;
  username?: string;
  date: number; // Store as timestamp
}

const DiaryEntryPage: React.FC<DiaryEntryPageProps> = ({ entryId, onClose }) => {
  const id = entryId;
  const isEditMode = id !== 'new' && id !== undefined;
  const dispatch = useAppDispatch();
  
  const currentUser = useAppSelector(selectUser);
  const allBrands = useAppSelector(selectAllBrands);
  const allDrinks = useAppSelector(selectAllDrinks);
  const allLocations = useAppSelector(selectAllLocations) as FirestoreLocation[];
  const allCurrentUserReviews = useAppSelector(reviewsSelectors.selectAll);
  
  // Use ReduxReview type and add missing properties with type assertion
  // Call useAppSelector unconditionally to adhere to Rules of Hooks
  const reviewFromStore = useAppSelector(state => (isEditMode && id) ? reviewsSelectors.selectById(state, id) : null);
  const existingReview = isEditMode ? reviewFromStore as (ReduxReview & { brandId?: string; brandName?: string }) | null : null;
    
  const operationLoading = useAppSelector(state => state.userSpecific.operationLoading);
  
  // Form state
  const [formData, setFormData] = useState<ReviewFormData>({
    brandId: existingReview?.brandId || '',
    brandName: existingReview?.brandName || '',
    drinkId: existingReview?.drinkId || '',
    drinkName: existingReview?.drinkName || '',
    rating: existingReview?.rating || 0,
    reviewText: existingReview?.reviewText || '',
    isPublic: existingReview?.isPublic ?? true,
    locationId: existingReview?.locationId || undefined,
    locationName: existingReview?.locationName || undefined,
    userId: existingReview?.userId || currentUser?.id || '',
    username: existingReview?.username || currentUser?.displayName || '',
    date: existingReview?.date || Date.now(),
  });

  // Get drinks for the selected brand
  const [brandDrinks, setBrandDrinks] = useState<Drink[]>([]);
  
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  
  // State for new modals
  const [isAddBrandModalOpen, setIsAddBrandModalOpen] = useState(false);
  const [isAddDrinkModalOpen, setIsAddDrinkModalOpen] = useState(false);
  
  // Error state
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLocationSelectOpen, setIsLocationSelectOpen] = useState(false);

  // Sort brands and drinks alphabetically without altering the objects
  const sortedBrands = useMemo(() => {
    // Sort without creating new objects - preserve original references
    return [...allBrands].sort((a, b) => {
      const nameA = (a.name || "").trim().toLowerCase();
      const nameB = (b.name || "").trim().toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [allBrands]);
  
  // Sort drinks for the selected brand alphabetically without altering the objects
  const sortedBrandDrinks = useMemo(() => {
    // Sort without creating new objects - preserve original references
    return [...brandDrinks].sort((a, b) => {
      const nameA = (a.name || "").trim().toLowerCase();
      const nameB = (b.name || "").trim().toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [brandDrinks]);
  
  // Update drinks list when brand changes
  useEffect(() => {
    if (formData.brandId) {
      // Get and sort the drinks for the selected brand
      // Don't modify the objects, just filter and sort
      const drinksForBrand = allDrinks
        .filter(drink => drink.brandId === formData.brandId)
        .sort((a, b) => {
          const nameA = (a.name || "").trim().toLowerCase();
          const nameB = (b.name || "").trim().toLowerCase();
          return nameA.localeCompare(nameB);
        });
      
      setBrandDrinks(drinksForBrand);
      
      // If current drink isn't from this brand, reset drink selection
      const drinkBelongsToBrand = drinksForBrand.some(
        drink => drink.id === formData.drinkId
      );
      
      if (!drinkBelongsToBrand) {
        setFormData(prev => ({ ...prev, drinkId: '', drinkName: '' }));
      }
    } else {
      setBrandDrinks([]);
    }
  }, [formData.brandId, allDrinks, formData.drinkId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBrandChange = (brandId: string) => {
    const selectedBrand = allBrands.find(brand => brand.id === brandId);
    setFormData(prev => ({ 
      ...prev, 
      brandId,
      brandName: selectedBrand?.name || ''
    }));
  };

  const handleNewBrandAdded = (brandId: string) => {
    const newBrand = allBrands.find(brand => brand.id === brandId);
    if (newBrand) {
      setFormData(prev => ({
        ...prev,
        brandId: newBrand.id!,
        brandName: newBrand.name,
        drinkId: '', // Reset drink selection
        drinkName: ''
      }));
      setIsAddBrandModalOpen(false);
      // Optionally, open the brand popover to show the new brand or directly select it
      // For now, we just update the form data
    }
  };

  const handleDrinkChange = (drinkId: string) => {
    const selectedDrink = allDrinks.find(drink => drink.id === drinkId);
    setFormData(prev => ({ 
      ...prev, 
      drinkId,
      drinkName: selectedDrink?.name || ''
    }));

    // If creating a new entry, check for existing reviews for this drink by the user
    if (!isEditMode && currentUser && selectedDrink) {
      const userReviewsForThisDrink = allCurrentUserReviews.filter(
        review => review.drinkId === selectedDrink.id && review.userId === currentUser.id
      );

      if (userReviewsForThisDrink.length > 0) {
        // Sort by date descending to get the most recent one
        userReviewsForThisDrink.sort((a, b) => b.date - a.date);
        toast.info(`You already have a review for ${selectedDrink.name}. Opening your most recent one.`);
        if (onClose) onClose();
        return; // Stop further processing for the new entry form
      }
    }
  };

  const handleNewDrinkAdded = (drinkId: string) => {
    const newDrink = allDrinks.find(drink => drink.id === drinkId);
    if (newDrink) {
      setFormData(prev => ({
        ...prev,
        drinkId: newDrink.id!,
        drinkName: newDrink.name
      }));
    }
    setIsAddDrinkModalOpen(false);
    // Optionally, open the drink popover to show the new drink or directly select it
  };

  const handleRatingChange = (rating: number) => {
    setFormData(prev => ({ ...prev, rating }));
  };
  
  const handleSliderChange = (values: number[]) => {
    if (values.length > 0) {
      const rating = values[0];
      setFormData(prev => ({ ...prev, rating }));
    }
  };
  
  const handleRatingInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value) && value >= 0 && value <= 5) {
      setFormData(prev => ({ ...prev, rating: value }));
    }
  };

  const handleToggleChange = (checked: boolean) => {
    setFormData(prev => ({ ...prev, isPublic: checked }));
  };
  
  const handleLocationSelectionChange = (selectedId: string | null | string[]) => {
    if (typeof selectedId === 'string' || selectedId === null) { // Single mode
      const selectedLocation = allLocations.find(loc => loc.id === selectedId);
      setFormData(prev => ({
        ...prev,
        locationId: selectedLocation?.id || undefined,
        locationName: selectedLocation?.name || undefined,
      }));
    }
  };

  const handleConfirmLocation = (selectedId: string | string[] | null) => {
    if (typeof selectedId === 'string' || selectedId === null) { // Single mode
        const selectedLocation = allLocations.find(loc => loc.id === selectedId);
        setFormData(prev => ({
            ...prev,
            locationId: selectedLocation?.id || undefined,
            locationName: selectedLocation?.name || undefined
        }));
    }
    setIsLocationSelectOpen(false);
  };
  
  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setFormData(prev => ({ ...prev, date: date.getTime() }));
      setIsDatePickerOpen(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    
    if (!currentUser?.id) {
      toast.error("You must be logged in to save entries");
      return;
    }

    if (!formData.brandId || !formData.drinkId) {
      toast.error("Please select a brand and drink");
      return;
    }

    try {
      // Get brand and drink names to store in the review
      const selectedBrand = allBrands.find(brand => brand.id === formData.brandId);
      const selectedDrink = allDrinks.find(drink => drink.id === formData.drinkId);

      if (!selectedBrand || !selectedDrink) {
        toast.error("Please select a valid brand and drink");
        return;
      }

      // Ensure all required fields are present
      const reviewData = {
        userId: currentUser.id,
        username: currentUser.displayName || 'Anonymous',
        drinkId: formData.drinkId,
        drinkName: selectedDrink.name,
        brandId: formData.brandId,
        brandName: selectedBrand.name,
        rating: formData.rating,
        reviewText: formData.reviewText || '',
        isPublic: formData.isPublic,
        // Only include locationId and locationName if they are defined
        ...(formData.locationId ? { 
          locationId: formData.locationId,
          locationName: formData.locationName
        } : {})
      };

      console.log("Submitting review data:", reviewData);

      if (isEditMode && id) {
        // Update existing review
        await dispatch(updateReview({ 
          id, 
          data: reviewData
        })).unwrap();
        toast.success("Your diary entry has been updated");
        if (onClose) onClose();
      } else {
        // Add new review
        await dispatch(addReview(reviewData)).unwrap();
        toast.success("Your diary entry has been saved");
        if (onClose) onClose();
      }
    } catch (error: any) {
      console.error("Error saving diary entry:", error);
      setErrorMessage(error.message || "Failed to save diary entry");
      toast.error(error.message || "Failed to save diary entry");
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!isEditMode || !id || !currentUser?.id) {
      toast.error("Cannot delete this entry.");
      return;
    }

    try {
      // Consider adding a confirmation dialog here in a real app
      await dispatch(deleteReview(id)).unwrap();
      toast.success("Diary entry deleted successfully");
      if (onClose) onClose();
    } catch (error: any) {
      console.error("Error deleting diary entry:", error);
      setErrorMessage(error.message || "Failed to delete diary entry");
      toast.error(error.message || "Failed to delete diary entry");
    }
  };
  
  const formattedDate = React.useMemo(() => {
    return formData.date ? format(new Date(formData.date), 'PPP') : 'Select date';
  }, [formData.date]);

  return (
    <div className="bg-card p-4 md:p-6 rounded-lg overflow-y-auto flex-grow">
      <h2 className="text-xl font-semibold mb-4">Diary Entry</h2>
      
      {errorMessage && (
        <div className="mb-4 p-4 border border-red-300 bg-red-50 text-red-800 rounded">
          <p className="font-medium">Error</p>
          <p>{errorMessage}</p>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="space-y-6">
            <div className="space-y-2 min-h-[80px]">
              <Label htmlFor="brand">Brand</Label>
              <SingleSelect
                items={sortedBrands || []}
                selectedId={formData.brandId}
                onSelectItem={handleBrandChange}
                placeholder="Select a brand"
                emptyMessage="No brands found"
                searchPlaceholder="Search brands..."
                showAddButton={true}
                onAddClick={() => setIsAddBrandModalOpen(true)}
                addButtonLabel="Add new brand"
              />
            </div>

            <div className="space-y-2 min-h-[80px]">
              <Label htmlFor="date">Date</Label>
              <PopoverDialog open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                <PopoverDialogTrigger asChild>
                  <Button
                    id="date"
                    type="button"
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formattedDate}
                  </Button>
                </PopoverDialogTrigger>
                <PopoverDialogContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={new Date(formData.date)}
                    onSelect={handleDateSelect}
                    initialFocus
                    disabled={(date) => {
                      // Disable dates in the future
                      return date > new Date();
                    }}
                  />
                </PopoverDialogContent>
              </PopoverDialog>
            </div>
          </div>

          <div className="space-y-6">
            <div className="space-y-2 min-h-[80px]">
              <Label htmlFor="drink">Drink</Label>
              <SingleSelect
                items={sortedBrandDrinks || []}
                selectedId={formData.drinkId}
                onSelectItem={handleDrinkChange}
                placeholder={formData.brandId ? "Select a drink" : "First select a brand"}
                emptyMessage="No drinks found"
                searchPlaceholder="Search drinks..."
                disabled={!formData.brandId}
                showAddButton={!!formData.brandId}
                onAddClick={() => setIsAddDrinkModalOpen(true)}
                addButtonLabel="Add new drink"
              />
            </div>
            
            <div className="space-y-2 min-h-[80px]">
              <Label htmlFor="location">Location</Label>
              <Button
                id="location-select-trigger"
                variant="outline"
                type="button"
                onClick={() => setIsLocationSelectOpen(true)}
                className="w-full justify-between"
              >
                {formData.locationName ? formData.locationName : "Select location..."}
                <MapPin className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </div>
          </div>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="rating">Rating</Label>
              <div className="space-y-6">
                <Slider
                  id="rating-slider"
                  max={5}
                  step={0.1}
                  value={[formData.rating]}
                  onValueChange={handleSliderChange}
                  className="w-full"
                />
                
                <div className="flex items-center justify-between">
                  <StarRating
                    value={formData.rating}
                    onChange={handleRatingChange}
                    size="lg"
                  />
                  
                  <Input
                    type="number"
                    id="rating-input"
                    value={formData.rating.toFixed(2)}
                    onChange={handleRatingInputChange}
                    min={0}
                    max={5}
                    step={0.01}
                    className="w-20 shrink-0"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="reviewText">Review</Label>
          <Textarea
            id="reviewText"
            name="reviewText"
            placeholder="Write your review here..."
            rows={10}
            value={formData.reviewText}
            onChange={handleInputChange}
            className="w-full"
          />
        </div>

        <div className="flex items-center justify-between pt-4">
          <div className="flex items-center space-x-2">
            <Switch
              id="isPublic"
              checked={formData.isPublic}
              onCheckedChange={handleToggleChange}
            />
            <Label htmlFor="isPublic" className="text-sm">Public Entry</Label>
          </div>
          
          <div className="flex items-center space-x-2">
            {isEditMode && (
              <Button
                type="button"
                variant="destructive"
                onClick={handleDelete}
                disabled={operationLoading.deleteReview === 'pending'}
              >
                {operationLoading.deleteReview === 'pending' ? (
                  'Deleting...'
                ) : (
                  <>
                    <Trash2 className="mr-2 h-4 w-4" /> Delete
                  </>
                )}
              </Button>
            )}
            <Button 
              type="submit" 
              disabled={
                formData.rating === 0 ||
                operationLoading.addReview === 'pending' || 
                operationLoading.updateReview === 'pending'
              }
            >
              {operationLoading.addReview === 'pending' || operationLoading.updateReview === 'pending'
                ? 'Saving...'
                : isEditMode ? 'Update Entry' : 'Submit'
              }
            </Button>
          </div>
        </div>
      </form>

      {/* Add Brand Modal */}
      <AddBrandModal
        isOpen={isAddBrandModalOpen}
        onClose={() => setIsAddBrandModalOpen(false)}
        onBrandAdded={handleNewBrandAdded}
      />

      {/* Add Drink Modal - only pass brandId if it exists */}
      {formData.brandId && (
        <AddDrinkModal
          isOpen={isAddDrinkModalOpen}
          onClose={() => setIsAddDrinkModalOpen(false)}
          onDrinkAdded={handleNewDrinkAdded}
          brandId={formData.brandId}
        />
      )}

      <Dialog open={isLocationSelectOpen} onOpenChange={setIsLocationSelectOpen}>
        <DialogContent className="sm:max-w-3xl md:max-w-4xl lg:max-w-5xl xl:max-w-6xl w-[95vw] max-h-[90vh] flex flex-col p-4">
          <DialogHeader>
            <DialogTitle>Select Location</DialogTitle>
          </DialogHeader>
          <div className="flex-grow overflow-hidden">
            <LocationSelect
              allLocations={allLocations}
              selectedLocationId={formData.locationId}
              onSelectionChange={handleLocationSelectionChange}
              mode="single"
              onConfirm={handleConfirmLocation}
              onCancel={() => setIsLocationSelectOpen(false)}
              containerClassName="h-full"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DiaryEntryPage; 